import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { AdminGuard } from "@/components/admin/AdminShell";
import { PaginationBar } from "@/components/admin/ListControls";
import { usePagedList } from "@/hooks/usePagedList";
import { TASK_STATUS_LABELS, INCIDENT_STATUS_LABELS, formatDate } from "@/constants/business_status";

function Badge({ children }) {
  return <span className="text-xs font-semibold bg-navy/5 text-navy rounded-full px-2.5 py-1 whitespace-nowrap">{children}</span>;
}

function InlineForm({ fields, onSubmit, testPrefix, submitLabel = "Créer" }) {
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 mb-4 flex flex-wrap gap-2 items-end" data-testid={`${testPrefix}-form`}>
      {fields.map((f) => (
        <div key={f.name} className="min-w-[140px]">
          <label className="text-[11px] font-semibold text-navy">{f.label}</label>
          {f.options ? (
            <Select value={values[f.name] || ""} onValueChange={(v) => setValues((p) => ({ ...p, [f.name]: v }))}>
              <SelectTrigger data-testid={`${testPrefix}-${f.name}`} className="h-9 bg-white rounded-lg"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{f.options.map((o) => <SelectItem key={o[0]} value={o[0]}>{o[1]}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input data-testid={`${testPrefix}-${f.name}`} type={f.type || "text"} value={values[f.name] || ""}
              onChange={(e) => setValues((p) => ({ ...p, [f.name]: e.target.value }))} className="h-9 bg-white rounded-lg" placeholder={f.placeholder} />
          )}
        </div>
      ))}
      <Button data-testid={`${testPrefix}-submit`} disabled={busy}
        onClick={async () => { setBusy(true); try { await onSubmit(values); setValues({}); } finally { setBusy(false); } }}
        className="bg-orange hover:bg-orange/90 text-white rounded-lg h-9"><Plus className="w-4 h-4 mr-1" /> {submitLabel}</Button>
    </div>
  );
}

function ListTable({ headers, rows, loading, emptyText, testId }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
      <Table data-testid={testId}>
        <TableHeader><TableRow>{headers.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {loading ? <TableRow><TableCell colSpan={headers.length} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy" /></TableCell></TableRow>
            : rows.length === 0 ? <TableRow><TableCell colSpan={headers.length} className="text-center py-8 text-muted-foreground">{emptyText}</TableCell></TableRow>
            : rows}
        </TableBody>
      </Table>
    </div>
  );
}

export function VehiclesPage() {
  const list = usePagedList("/admin/vehicles", {}, []);
  const create = async (v) => {
    if (!v.registration) return toast.error("Immatriculation requise");
    try {
      await apiClient.post("/admin/vehicles", { ...v, useful_volume_m3: v.useful_volume_m3 ? parseFloat(v.useful_volume_m3) : null });
      toast.success("Véhicule créé"); list.refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    await apiClient.patch(`/admin/vehicles/${id}`, { registration: undefined, status }).catch(() => {});
    list.refresh();
  };
  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-4">Véhicules</h1>
      <InlineForm testPrefix="vehicle" onSubmit={create} fields={[
        { name: "registration", label: "Immatriculation", placeholder: "AB-123-CD" },
        { name: "brand", label: "Marque" }, { name: "model", label: "Modèle" },
        { name: "category", label: "Catégorie", options: [["fourgonnette", "Fourgonnette"], ["fourgon_12m3", "Fourgon 12 m³"], ["fourgon_20m3", "Fourgon 20 m³"], ["camion_hayon", "Camion hayon"], ["autre", "Autre"]] },
        { name: "useful_volume_m3", label: "Volume utile (m³)", type: "number" },
      ]} />
      <ListTable testId="vehicles-table" loading={list.loading} emptyText="Aucun véhicule"
        headers={["Immatriculation", "Marque / Modèle", "Catégorie", "Volume", "Contrôle technique", "Statut", ""]}
        rows={list.items.map((v) => (
          <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
            <TableCell className="font-semibold text-navy">{v.registration}</TableCell>
            <TableCell className="text-sm">{[v.brand, v.model].filter(Boolean).join(" ") || "—"}</TableCell>
            <TableCell className="text-xs">{v.category}</TableCell>
            <TableCell className="text-xs">{v.useful_volume_m3 ? `${v.useful_volume_m3} m³` : "—"}</TableCell>
            <TableCell className="text-xs">{v.technical_inspection_date || "—"}</TableCell>
            <TableCell>
              <Select value={v.status} onValueChange={(s) => setStatus(v.id, s)}>
                <SelectTrigger className="h-8 w-36 text-xs bg-white rounded-lg" data-testid={`vehicle-status-${v.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{["disponible", "reserve", "en_mission", "entretien", "indisponible"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </TableCell>
            <TableCell><Button size="sm" variant="ghost" className="text-red-600 text-xs" data-testid={`vehicle-archive-${v.id}`}
              onClick={async () => { await apiClient.post(`/admin/vehicles/${v.id}/archive`); list.refresh(); }}>Archiver</Button></TableCell>
          </TableRow>
        ))} />
      <PaginationBar total={list.total} page={list.page} pageSize={list.pageSize} totalPages={list.total_pages} onPage={list.setPage} onSize={list.setPageSize} loading={list.loading} testPrefix="vehicles-" />
    </AdminGuard>
  );
}

export function TeamsAndUsersPage() {
  const teams = usePagedList("/admin/teams", {}, []);
  const [users, setUsers] = useState(null);
  const [rolesMeta, setRolesMeta] = useState({});
  useState(() => {
    apiClient.get("/admin/users").then((r) => { setUsers(r.data.items); setRolesMeta(r.data.roles); }).catch(() => setUsers([]));
  });
  const createTeam = async (v) => {
    if (!v.name) return toast.error("Nom requis");
    try { await apiClient.post("/admin/teams", v); toast.success("Équipe créée"); teams.refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const createUser = async (v) => {
    if (!v.email || !v.name || !v.password) return toast.error("Email, nom et mot de passe requis");
    try {
      await apiClient.post("/admin/users", { ...v, role: v.role || "readonly" });
      toast.success("Utilisateur créé");
      const r = await apiClient.get("/admin/users"); setUsers(r.data.items);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-4">Équipes &amp; utilisateurs</h1>
      <InlineForm testPrefix="team" onSubmit={createTeam} fields={[
        { name: "name", label: "Nom de l'équipe", placeholder: "Équipe A" },
        { name: "availability_notes", label: "Disponibilités" },
      ]} />
      <ListTable testId="teams-table" loading={teams.loading} emptyText="Aucune équipe"
        headers={["Nom", "Membres", "Compétences", "Statut"]}
        rows={teams.items.map((t) => (
          <TableRow key={t.id} data-testid={`team-row-${t.id}`}>
            <TableCell className="font-semibold text-navy">{t.name}</TableCell>
            <TableCell className="text-xs">{(t.member_ids || []).length}</TableCell>
            <TableCell className="text-xs">{(t.skills || []).join(", ") || "—"}</TableCell>
            <TableCell><Badge>{t.status}</Badge></TableCell>
          </TableRow>
        ))} />

      <h2 className="font-bold text-navy text-lg mt-8 mb-3">Utilisateurs du CRM</h2>
      {users !== null && (
        <>
          <InlineForm testPrefix="user" onSubmit={createUser} submitLabel="Créer le compte" fields={[
            { name: "email", label: "E-mail" }, { name: "name", label: "Nom" },
            { name: "password", label: "Mot de passe", type: "password" },
            { name: "role", label: "Rôle", options: Object.entries(rolesMeta) },
            { name: "phone", label: "Téléphone" },
          ]} />
          <ListTable testId="users-table" loading={false} emptyText="Accès réservé à la gestion des utilisateurs"
            headers={["E-mail", "Nom", "Rôle", "Actif", ""]}
            rows={users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-semibold text-navy text-sm">{u.email}</TableCell>
                <TableCell className="text-sm">{u.name || "—"}</TableCell>
                <TableCell><Badge>{rolesMeta[u.role] || u.role}</Badge></TableCell>
                <TableCell className="text-xs">{u.active === false ? "Désactivé" : "Actif"}</TableCell>
                <TableCell><Button size="sm" variant="ghost" className="text-xs" data-testid={`user-toggle-${u.id}`}
                  onClick={async () => {
                    try { await apiClient.patch(`/admin/users/${u.id}`, { active: u.active === false }); const r = await apiClient.get("/admin/users"); setUsers(r.data.items); }
                    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                  }}>{u.active === false ? "Réactiver" : "Désactiver"}</Button></TableCell>
              </TableRow>
            ))} />
        </>
      )}
    </AdminGuard>
  );
}

export function TasksPage() {
  const [view, setView] = useState("all");
  const params = view !== "all" ? { view } : {};
  const list = usePagedList("/admin/tasks", params, [view]);
  const create = async (v) => {
    if (!v.title) return toast.error("Titre requis");
    try { await apiClient.post("/admin/tasks", v); toast.success("Tâche créée"); list.refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await apiClient.patch(`/admin/tasks/${id}`, { status }); list.refresh(); };
  return (
    <AdminGuard>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h1 className="font-heading font-extrabold text-2xl text-navy mr-auto">Tâches</h1>
        {[["all", "Toutes"], ["mine", "Mes tâches"], ["late", "En retard"], ["today", "Aujourd'hui"], ["week", "Cette semaine"]].map(([k, l]) => (
          <button key={k} data-testid={`tasks-view-${k}`} onClick={() => setView(k)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${view === k ? "bg-navy text-white" : "bg-white text-navy"}`}>{l}</button>
        ))}
      </div>
      <InlineForm testPrefix="task" onSubmit={create} fields={[
        { name: "title", label: "Titre" },
        { name: "priority", label: "Priorité", options: [["low", "Basse"], ["normal", "Normale"], ["high", "Haute"], ["urgent", "Urgente"]] },
        { name: "assignee_email", label: "Assignée à (e-mail)" },
        { name: "due_date", label: "Échéance", type: "date" },
      ]} />
      <ListTable testId="tasks-table" loading={list.loading} emptyText="Aucune tâche"
        headers={["Titre", "Priorité", "Assignée à", "Échéance", "Statut", ""]}
        rows={list.items.map((t) => (
          <TableRow key={t.id} data-testid={`task-row-${t.id}`}>
            <TableCell className="font-medium text-sm">{t.title}</TableCell>
            <TableCell><Badge>{t.priority}</Badge></TableCell>
            <TableCell className="text-xs">{t.assignee_email || "—"}</TableCell>
            <TableCell className="text-xs">{t.due_date || "—"}</TableCell>
            <TableCell><Badge>{TASK_STATUS_LABELS[t.status] || t.status}</Badge></TableCell>
            <TableCell>
              {t.status !== "done" && <Button data-testid={`task-done-${t.id}`} size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setStatus(t.id, "done")}>Terminer</Button>}
            </TableCell>
          </TableRow>
        ))} />
      <PaginationBar total={list.total} page={list.page} pageSize={list.pageSize} totalPages={list.total_pages} onPage={list.setPage} onSize={list.setPageSize} loading={list.loading} testPrefix="tasks-" />
    </AdminGuard>
  );
}

export function IncidentsPage() {
  const list = usePagedList("/admin/incidents", {}, []);
  const create = async (v) => {
    if (!v.description) return toast.error("Description requise");
    try { await apiClient.post("/admin/incidents", { ...v, incident_type: v.incident_type || "autre" }); toast.success("Incident déclaré"); list.refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await apiClient.patch(`/admin/incidents/${id}`, { status }); list.refresh(); };
  return (
    <AdminGuard>
      <h1 className="font-heading font-extrabold text-2xl text-navy mb-4">Incidents &amp; réclamations</h1>
      <InlineForm testPrefix="incident" onSubmit={create} submitLabel="Déclarer" fields={[
        { name: "incident_type", label: "Type", options: [["retard", "Retard"], ["panne", "Panne"], ["dommage", "Dommage"], ["casse", "Casse"], ["acces_impossible", "Accès impossible"], ["volume_superieur", "Volume supérieur"], ["reclamation", "Réclamation"], ["autre", "Autre"]] },
        { name: "severity", label: "Gravité", options: [["mineur", "Mineur"], ["modere", "Modéré"], ["majeur", "Majeur"], ["critique", "Critique"]] },
        { name: "description", label: "Description" },
      ]} />
      <ListTable testId="incidents-table" loading={list.loading} emptyText="Aucun incident"
        headers={["Date", "Type", "Gravité", "Description", "Statut", ""]}
        rows={list.items.map((i) => (
          <TableRow key={i.id} data-testid={`incident-row-${i.id}`}>
            <TableCell className="text-xs">{formatDate(i.created_at)}</TableCell>
            <TableCell><Badge>{i.incident_type}</Badge></TableCell>
            <TableCell className="text-xs">{i.severity}</TableCell>
            <TableCell className="text-sm max-w-xs truncate">{i.description}</TableCell>
            <TableCell><Badge>{INCIDENT_STATUS_LABELS[i.status] || i.status}</Badge></TableCell>
            <TableCell>
              <Select value={i.status} onValueChange={(s) => setStatus(i.id, s)}>
                <SelectTrigger className="h-8 w-36 text-xs bg-white rounded-lg" data-testid={`incident-status-${i.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(INCIDENT_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </TableCell>
          </TableRow>
        ))} />
      <PaginationBar total={list.total} page={list.page} pageSize={list.pageSize} totalPages={list.total_pages} onPage={list.setPage} onSize={list.setPageSize} loading={list.loading} testPrefix="incidents-" />
    </AdminGuard>
  );
}
